import { z } from "zod";
import { differenceInMinutes } from "date-fns";
import { addMinutesToTimeHhMm } from "@/shared/lib/date-time";
import {
  PRIVATE_LESSON_SLOT_DURATION_SET,
  type PrivateLessonSlotDurationMinutes,
} from "@/shared/lib/private-lesson-slot-duration";
import {
  formatQuarterHhMm,
  isCanonicalQuarterHhMm,
  parseQuarterClock,
} from "@/shared/lib/quarter-time";

const studioSlotMinutesSchema = z.union([
  z.literal(15),
  z.literal(30),
  z.literal(45),
  z.literal(60),
  z.literal(75),
  z.literal(90),
  z.literal(105),
  z.literal(120),
  z.literal(135),
  z.literal(150),
  z.literal(165),
  z.literal(180),
]);

function refineStudioBookingFields(
  data: {
    bookStudio?: boolean;
    studioRoomId?: string;
    studioStartTime?: string;
  },
  ctx: z.RefinementCtx,
) {
  if (data.bookStudio) {
    if (!data.studioRoomId?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເລືອກຫ້ອງ Studio",
        path: ["studioRoomId"],
      });
    }
    if (!data.studioStartTime?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເລືອກເວລາເລີ່ມ",
        path: ["studioStartTime"],
      });
    } else if (!isCanonicalQuarterHhMm(data.studioStartTime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເວລາເລີ່ມຕົ້ນຕ້ອງເປັນນາທີ 00 / 15 / 30 / 45",
        path: ["studioStartTime"],
      });
    }
  }
}

export type RehearsalStudioFormSlice = {
  bookStudio?: boolean;
  studioRoomId?: string;
  studioStartTime?: string;
  studioSlotMinutes: z.infer<typeof studioSlotMinutesSchema>;
  scheduledDates: string[];
};

export function toStudioTimeHhMm(value?: string | null): string {
  if (!value?.trim()) return "09:00";
  const raw = value.length >= 8 ? value.slice(0, 5) : value;
  const { h, m } = parseQuarterClock(raw);
  return formatQuarterHhMm(h, m);
}

export function inferStudioSlotMinutes(
  startTime?: string | null,
  endTime?: string | null,
): z.infer<typeof studioSlotMinutesSchema> {
  if (!startTime?.trim() || !endTime?.trim()) return 60;
  const start = toStudioTimeHhMm(startTime);
  const end = toStudioTimeHhMm(endTime);
  try {
    const mins = differenceInMinutes(
      new Date(`2000-01-01T${end}`),
      new Date(`2000-01-01T${start}`),
    );
    if (mins > 0 && PRIVATE_LESSON_SLOT_DURATION_SET.has(mins)) {
      return mins as PrivateLessonSlotDurationMinutes;
    }
  } catch {
    /* ignore */
  }
  return 60;
}

export function buildStudioBookingPayloadFromSlice(
  values: Pick<
    RehearsalStudioFormSlice,
    "bookStudio" | "studioRoomId" | "studioStartTime" | "studioSlotMinutes"
  >,
) {
  if (!values.bookStudio || !values.studioRoomId || !values.studioStartTime) {
    return undefined;
  }
  const endTime = addMinutesToTimeHhMm(
    values.studioStartTime,
    values.studioSlotMinutes,
  );
  return {
    roomId: values.studioRoomId,
    startTime: values.studioStartTime,
    endTime,
    chargeType: "free" as const,
  };
}

export const createRehearsalFormSchema = z
  .object({
    performerIds: z.array(z.string()).min(1, "ເລືອກຢ່າງໜ້ອຍ 1 ຄົນ"),
    scheduledDates: z.array(z.string().min(1)).min(1, "ເລືອກຢ່າງໜ້ອຍ 1 ວັນຊ້ອມ"),
    location: z.string().optional(),
    status: z.enum(["planned", "completed", "cancelled"]),
    notes: z.string().optional(),
    bookStudio: z.boolean().optional(),
    studioRoomId: z.string().optional(),
    studioStartTime: z.string().optional(),
    studioSlotMinutes: studioSlotMinutesSchema,
  })
  .superRefine((data, ctx) => refineStudioBookingFields(data, ctx));

export type CreateFormValues = z.infer<typeof createRehearsalFormSchema>;

export const editRehearsalFormSchema = z.object({
  performerId: z.string().min(1, "ເລືອກນັກສະແດງ"),
  scheduledDate: z.string().min(1, "ເລືອກວັນທີ"),
  location: z.string().optional(),
  status: z.enum(["planned", "completed", "cancelled"]),
  notes: z.string().optional(),
});

export type EditFormValues = z.infer<typeof editRehearsalFormSchema>;

export function buildStudioBookingPayload(values: CreateFormValues) {
  return buildStudioBookingPayloadFromSlice(values);
}

export const rescheduleDayFormSchema = z
  .object({
    scheduledDate: z.string().min(1, "ເລືອກວັນໃໝ່"),
    bookStudio: z.boolean().optional(),
    studioRoomId: z.string().optional(),
    studioStartTime: z.string().optional(),
    studioSlotMinutes: studioSlotMinutesSchema,
  })
  .superRefine((data, ctx) => refineStudioBookingFields(data, ctx));

export type RescheduleDayFormValues = z.infer<typeof rescheduleDayFormSchema>;

export function buildRescheduleDefaultValues(input: {
  fromScheduledDate: string;
  studioSession?: {
    roomId?: string | null;
    bookingStartTime?: string | null;
    bookingEndTime?: string | null;
  } | null;
}): RescheduleDayFormValues {
  const hasStudio = Boolean(input.studioSession?.roomId);
  const startTime = toStudioTimeHhMm(input.studioSession?.bookingStartTime);
  return {
    scheduledDate: input.fromScheduledDate,
    bookStudio: hasStudio,
    studioRoomId: input.studioSession?.roomId ?? "",
    studioStartTime: startTime,
    studioSlotMinutes: inferStudioSlotMinutes(
      input.studioSession?.bookingStartTime,
      input.studioSession?.bookingEndTime,
    ),
  };
}

export function toRescheduleConflictValues(
  values: RescheduleDayFormValues,
): RehearsalStudioFormSlice {
  return {
    bookStudio: values.bookStudio,
    studioRoomId: values.studioRoomId,
    studioStartTime: values.studioStartTime,
    studioSlotMinutes: values.studioSlotMinutes,
    scheduledDates: values.scheduledDate ? [values.scheduledDate] : [],
  };
}

export function buildCreateDefaultValues(
  eventPerformers: { id: string }[],
  defaultDate?: string,
  defaultRoomId?: string | null,
): CreateFormValues {
  return {
    performerIds: eventPerformers.map((p) => p.id),
    scheduledDates: defaultDate ? [defaultDate] : [],
    location: "",
    status: "planned",
    notes: "",
    bookStudio: !!defaultRoomId,
    studioRoomId: defaultRoomId ?? "",
    studioStartTime: "09:00",
    studioSlotMinutes: 60,
  };
}
